import moment from "moment";
import { useMemo } from "react";
import useAllTransactions from "./useAllTransactions";
import useMainFilter from "./useMainFilter";

const DAYS_BEFORE_DUE_NOTIFICATION = 3;
const DAYS_AFTER_DUE_NOTIFICATION = -7;

export default function useRecurringTransactions(
  onlyCurrentAccount: boolean = true
) {
  const { mainFilter } = useMainFilter();
  const {
    allActiveTransactions,
    isLoadingAllTransactions,
    isRefetchingAllTransactions,
  } = useAllTransactions();

  const {
    allRecurringPayments,
    upcomingPayments,
    recentOverduePayments,
    scheduledPayments,
    todayPayments,
  } = useMemo(() => {
    if (
      [!!isLoadingAllTransactions /*!!isRefetchingAllTransactions*/].includes(
        true
      )
    ) {
      return {
        allRecurringPayments: [],
        upcomingPayments: [],
        recentOverduePayments: [],
        scheduledPayments: [],
        todayPayments: [],
      };
    }

    const today = moment().startOf("day");

    const result = allActiveTransactions?.reduce(
      (
        acc: {
          allRecurringPayments: any[];
          upcomingPayments: any[];
          recentOverduePayments: any[];
          scheduledPayments: any[];
          todayPayments: any[];
        },
        tx: any
      ) => {
        const { recurring } = tx;

        // ⛔ Ignorar si no tiene próxima fecha
        if (!recurring?.nextPayment) return acc;

        // ⛔ Filtrar por cuenta seleccionada (si aplica)
        if (onlyCurrentAccount) {
          if (mainFilter?.account && tx.account !== mainFilter.account)
            return acc;
        }

        const nextPayment = moment(recurring.nextPayment).startOf("day");
        const diffDays = nextPayment.diff(today, "days");

        const enrichedTx = {
          ...tx,
          daysUntilDue: diffDays >= 0 ? diffDays : undefined,
          daysOverdue: diffDays < 0 ? Math.abs(diffDays) : undefined,
          today: diffDays === 0,
        };

        // ✅ Siempre incluir en la lista general
        acc.allRecurringPayments.push(enrichedTx);

        // ⛔ Solo clasificar si está activado
        if (!recurring.enabled) return acc;

        if (diffDays === 0) {
          acc.todayPayments.push(enrichedTx);
        } else if (diffDays > 0 && diffDays <= DAYS_BEFORE_DUE_NOTIFICATION) {
          acc.upcomingPayments.push(enrichedTx);
        } else if (
          diffDays < 0 /* && diffDays >= DAYS_AFTER_DUE_NOTIFICATION */
        ) {
          acc.recentOverduePayments.push(enrichedTx);
        } else if (diffDays > DAYS_BEFORE_DUE_NOTIFICATION) {
          acc.scheduledPayments.push(enrichedTx);
        }

        return acc;
      },
      {
        allRecurringPayments: [],
        upcomingPayments: [],
        todayPayments: [],
        recentOverduePayments: [],
        scheduledPayments: [],
      }
    );

    // Ordenar por fecha de próximo pago
    const sortByNextPayment = (a: any, b: any) =>
      moment(a.recurring.nextPayment).diff(moment(b.recurring.nextPayment));

    result.allRecurringPayments.sort(sortByNextPayment);
    result.upcomingPayments.sort(sortByNextPayment);
    result.recentOverduePayments.sort(sortByNextPayment);
    result.scheduledPayments.sort(sortByNextPayment);

    return result;
  }, [
    allActiveTransactions,
    isLoadingAllTransactions,
    isRefetchingAllTransactions,
    mainFilter?.account,
    onlyCurrentAccount,
  ]);

  return {
    allRecurringPayments,
    upcomingPayments,
    recentOverduePayments,
    scheduledPayments,
    todayPayments,
  };
}
